import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { get } from 'idb-keyval';
import { useTranslation } from '../hooks/useTranslation';
import { locales } from '../locales';
import { makeTr, trDyn } from '../services/trLookup';
import BarcodeBild from './BarcodeBild';
import ZoomImageViewer from './ZoomImageViewer';
import BatchTutorialVideo from './BatchTutorialVideo';
import {
  BATCH_GAP_MS,
  STAPEL_DIENSTE_REIHENFOLGE,
  listBatch,
  removeFromBatch,
  getBatchPos,
  setBatchPos,
  getBatchExpectedCount,
  setBatchExpectedCount,
  markBatchItemScanned,
  resetBatchScanned,
  completeCurrentBatch,
  completeBatchByCarrier,
  markHistoryBatchCompleted,
  markPackageGroupCompleted,
  mergeHistoryIntoActive,
  listBatchHistory,
  restoreBatch,
  syncBatchFromServer,
  openBatchFromPackages,
  disableBatchBarcodesFeature,
  erkenneStapelDienst,
  groupBatchByCarrier,
  getStapelDienstStil,
  getBatchDisabledCarriers,
  setBatchDisabledCarriers,
  toggleBatchCarrierEnabled,
  isBatchCarrierEnabled,
} from '../services/barcodeBatch';
import {
  nativeMicPermission,
  nativeKeepAwake,
  nativeSetBrightness,
  nativeOpenBatteryOpt,
  nativeCamSupported,
} from '../services/stationCamera';
import { getAccessToken, refreshAccessToken } from '../services/apiService';
import { SERVER_URL } from '../config/serverConfig';

/**
 * Lädt das Originalfoto eines Pakets vom Server (für den Zoom-Viewer im Stapel).
 */
async function ladePaketVollbild(packageOrImageId) {
  if (!packageOrImageId) throw new Error('Kein Foto verknüpft');
  let token = getAccessToken();
  const tryFetch = async (url) => {
    let r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (r.status === 401) {
      try { await refreshAccessToken(); token = getAccessToken(); } catch { /* */ }
      r = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    }
    return r;
  };
  let r = await tryFetch(`${SERVER_URL}/packages/${encodeURIComponent(packageOrImageId)}/image?size=full`);
  if (!r.ok) {
    r = await tryFetch(`${SERVER_URL}/ml/images/${encodeURIComponent(packageOrImageId)}`);
  }
  if (!r.ok) throw new Error('Foto nicht gefunden');
  return URL.createObjectURL(await r.blob());
}

function formatUhrzeit(ts, lang = 'de') {
  if (!ts) return '';
  try {
    const loc = lang === 'nl' ? 'nl-NL' : lang === 'en' ? 'en-GB' : (lang || 'de-DE');
    return new Date(ts).toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

function formatDayAndRange(startTs, endTs, lang = 'de') {
  const s = startTs || endTs || Date.now();
  const e = endTs || startTs || s;
  const dt = new Date(s);
  const todayStr = new Date().toDateString();
  const yestStr = new Date(Date.now() - 86400000).toDateString();
  const dayStr = dt.toDateString();
  const loc = lang === 'nl' ? 'nl-NL' : lang === 'en' ? 'en-GB' : (lang || 'de-DE');
  const dayLabel = dayStr === todayStr
    ? (locales[lang]?.dates?.today || (lang === 'nl' ? 'Vandaag' : lang === 'en' ? 'Today' : 'Heute'))
    : dayStr === yestStr
      ? (locales[lang]?.dates?.yesterday || (lang === 'nl' ? 'Gisteren' : lang === 'en' ? 'Yesterday' : 'Gestern'))
      : dt.toLocaleDateString(loc, { weekday: 'short', day: '2-digit', month: '2-digit' });
  const tStart = formatUhrzeit(s, lang);
  const tEnd = formatUhrzeit(e, lang);
  const uhrSuffix = lang === 'de' ? ' Uhr' : '';
  const timeRange = tStart === tEnd ? `${tStart}${uhrSuffix}` : `${tStart} – ${tEnd}${uhrSuffix}`;
  return { dayLabel, timeRange, fullLabel: `${dayLabel}, ${timeRange}` };
}

/**
 * 📋 Barcode-Stapel: nach einer Kurierlieferung alle Codes nacheinander groß zeigen,
 * damit das Zusteller-Gerät sie abscannen kann (bei DHL: „Schnelleinlagerung").
 */
export default function BatchView({ onClose, t: tProp, colors, pakete = [], initialHistoryOpen = false, initialCarrier = '' }) {
  const { t: tCtx, language: ctxLang } = useTranslation();
  const t = tProp || tCtx;
  const lang = ctxLang || 'de';
  const tr = makeTr(lang);
  const uhr = (ts) => {
    const f = formatUhrzeit(ts, lang);
    if (!f) return '';
    return lang === 'de' ? `${f} Uhr` : f;
  };
  const pkgWord = (n) => (n === 1 ? tr('Paket', 'parcel', 'pakket') : tr('Pakete', 'parcels', 'pakketten'));

  const [liste, setListe] = useState(() => listBatch());
  const [selectedCarrier, setSelectedCarrier] = useState(initialCarrier || '');
  const [showCarrierSettings, setShowCarrierSettings] = useState(false);
  const [disabledCarriers, setDisabledCarriersState] = useState(() => getBatchDisabledCarriers());
  const [pos, setPosState] = useState(() => {
    const initial = listBatch();
    if (initialCarrier && initial.length > 0) {
      const targetKey = initialCarrier === '__other__' ? '' : initialCarrier;
      const firstOpen = initial.findIndex((x) => !x.scanned && erkenneStapelDienst(x).toLowerCase() === targetKey.toLowerCase());
      const firstAny = initial.findIndex((x) => erkenneStapelDienst(x).toLowerCase() === targetKey.toLowerCase());
      const jump = firstOpen >= 0 ? firstOpen : firstAny;
      if (jump >= 0) return jump;
    }
    return Math.min(getBatchPos(), Math.max(0, initial.length - 1));
  });
  const [expectedCount, setExpectedCountState] = useState(() => getBatchExpectedCount());
  const [showExpectedInput, setShowExpectedInput] = useState(false);
  const [expectedInputVal, setExpectedInputVal] = useState('');
  const [wahl, setWahl] = useState({}); // id → gewählter Code-Index
  const [uebersicht, setUebersicht] = useState(false);
  const [showHistory, setShowHistory] = useState(initialHistoryOpen);
  const [historyList, setHistoryList] = useState(() => listBatchHistory());
  const [idbPakete, setIdbPakete] = useState([]);
  const [idbChronik, setIdbChronik] = useState([]);
  const [filterDate, setFilterDate] = useState(''); // YYYY-MM-DD
  const [filterTimeFrom, setFilterTimeFrom] = useState(''); // HH:MM
  const [filterTimeTo, setFilterTimeTo] = useState(''); // HH:MM

  // 🖼️ Zoombares Originalfoto
  const [zoomSrc, setZoomSrc] = useState(null);
  const [fotoLoading, setFotoLoading] = useState(false);
  const [fotoError, setFotoError] = useState('');

  // 🎙️ Mikrofon Piep-Erkennung (DHL-Scanner-Piepton → grün + weiter)
  const [micOn, setMicOn] = useState(false);
  const [micError, setMicError] = useState('');
  const [beepFlash, setBeepFlash] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const audioCtxRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const lastBeepTsRef = useRef(0);
  const hitFramesRef = useRef(0);
  const currentItemIdRef = useRef(null);

  const dark = colors?.darkMode;

  const setPos = useCallback((updater) => {
    setPosState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const clean = Math.max(0, next);
      setBatchPos(clean);
      return clean;
    });
  }, []);

  // 🔆 Automatisches Abdunkeln deaktivieren & volle Helligkeit aktivieren, solange der Barcode-Stapel offen ist
  useEffect(() => {
    let wakeLockSentinel = null;
    let active = true;

    nativeKeepAwake(true).catch(() => {});
    nativeSetBrightness(1.0).catch(() => {});

    const requestWakeLock = async () => {
      if (!active) return;
      try {
        if (navigator.wakeLock && typeof navigator.wakeLock.request === 'function') {
          wakeLockSentinel = await navigator.wakeLock.request('screen');
        }
      } catch { /* ignore */ }
    };
    requestWakeLock();

    const onVisChange = () => {
      if (document.visibilityState === 'visible' && active) {
        requestWakeLock();
        nativeKeepAwake(true).catch(() => {});
        nativeSetBrightness(1.0).catch(() => {});
      }
    };
    document.addEventListener('visibilitychange', onVisChange);

    return () => {
      active = false;
      document.removeEventListener('visibilitychange', onVisChange);
      if (wakeLockSentinel) {
        try { wakeLockSentinel.release(); } catch { /* */ }
      }
      nativeKeepAwake(false).catch(() => {});
      nativeSetBrightness(-1).catch(() => {});
    };
  }, []);

  useEffect(() => {
    syncBatchFromServer().then(() => {
      const fresh = listBatch();
      setListe(fresh);
      setHistoryList(listBatchHistory());
      setExpectedCountState(getBatchExpectedCount());
      setDisabledCarriersState(getBatchDisabledCarriers());
    }).catch(() => {});
    get('pakete').then((p) => { if (Array.isArray(p)) setIdbPakete(p); }).catch(() => {});
    get('chronik').then((c) => { if (Array.isArray(c)) setIdbChronik(c); }).catch(() => {});
    const upd = () => {
      const fresh = listBatch();
      setListe(fresh);
      setHistoryList(listBatchHistory());
      setExpectedCountState(getBatchExpectedCount());
      setDisabledCarriersState(getBatchDisabledCarriers());
      setPosState((cur) => {
        const curId = currentItemIdRef.current;
        if (curId) {
          const newIdx = fresh.findIndex((x) => x.id === curId);
          if (newIdx >= 0) {
            if (newIdx !== cur) setBatchPos(newIdx, false);
            return newIdx;
          }
        }
        return Math.min(cur, Math.max(0, fresh.length - 1));
      });
    };
    window.addEventListener('kiosk:batch-changed', upd);
    return () => window.removeEventListener('kiosk:batch-changed', upd);
  }, []);

  // 📦 Automatische Liefer-Sitzungen (15-Minuten-Abstand!) aus den erfassten Paketen der letzten 7 Tage,
  // die noch nicht im aktuellen Stapel oder in der Stapel-Historie enthalten sind
  const allePakete = useMemo(() => (pakete && pakete.length > 0 ? pakete : idbPakete), [pakete, idbPakete]);
  const dailyPackageGroups = useMemo(() => {
    const cutoff = Date.now() - 7 * 24 * 60 * 60 * 1000;
    // Bereits in active oder history enthaltene Pakete merken (damit keine Lieferung doppelt auftaucht)
    const coveredTrackings = new Set();
    const coveredPaketIds = new Set();
    for (const it of liste || []) {
      if (it?.tracking) coveredTrackings.add(String(it.tracking).trim().toUpperCase());
      if (it?.paketId) coveredPaketIds.add(String(it.paketId));
      if (it?.id) coveredPaketIds.add(String(it.id));
    }
    for (const h of historyList || []) {
      for (const it of h?.items || []) {
        if (it?.tracking) coveredTrackings.add(String(it.tracking).trim().toUpperCase());
        if (it?.paketId) coveredPaketIds.add(String(it.paketId));
        if (it?.id) coveredPaketIds.add(String(it.id));
      }
    }

    const byId = new Map();
    for (const p of allePakete || []) {
      if (!p) continue;
      const ts = p.datum ? new Date(p.datum).getTime() : 0;
      if (!ts || ts < cutoff) continue;
      const trk = String(p.trackingNumber || p.trackingNummer || '').trim();
      const normTrk = trk.toLowerCase() === 'keine' ? '' : trk;
      const pDienst = erkenneStapelDienst({ dienst: p.paketdienst || '', tracking: normTrk, codes: p.batchCodes || [] });
      if (pDienst && !isBatchCarrierEnabled(pDienst)) continue;
      if ((p.id && coveredPaketIds.has(String(p.id))) || (normTrk && coveredTrackings.has(normTrk.toUpperCase()))) continue;
      byId.set(p.id || `${ts}-${p.name}`, {
        id: p.id,
        paketId: p.id,
        name: p.name || '',
        regalplatz: p.regalplatz || p.regal || '',
        trackingNumber: normTrk,
        paketdienst: p.paketdienst || pDienst || '',
        batchCodes: Array.isArray(p.batchCodes) ? p.batchCodes : undefined,
        postalAndCity: p.postalAndCity || '',
        imageUrl: p.imageUrl || p.image_url || '',
        timestamp: ts,
      });
    }
    for (const c of idbChronik || []) {
      if (!c || c.action !== 'paket_hinzugefuegt') continue;
      const ts = c.timestamp || 0;
      if (!ts || ts < cutoff) continue;
      const key = c.paketId || c.id;
      if (byId.has(key)) continue;
      const trk = String(c.trackingId || '').trim();
      const normTrk = trk.toLowerCase() === 'keine' ? '' : trk;
      if ((key && coveredPaketIds.has(String(key))) || (normTrk && coveredTrackings.has(normTrk.toUpperCase()))) continue;
      byId.set(key, {
        id: key,
        paketId: c.paketId || null,
        name: c.paketName || '',
        regalplatz: c.regalplatz || '',
        trackingNumber: normTrk,
        postalAndCity: '',
        imageUrl: c.paketId || '',
        timestamp: ts,
      });
    }

    // Nach Zeit sortieren und bei > 15 Minuten Abstand (BATCH_GAP_MS) in eigene Liefer-Stapel trennen!
    const sorted = Array.from(byId.values()).sort((a, b) => a.timestamp - b.timestamp);
    const rawSessions = [];
    for (const item of sorted) {
      const d = new Date(item.timestamp);
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      const dayKey = `${yyyy}-${mm}-${dd}`;
      const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
      if (filterDate && dayKey !== filterDate) continue;
      if (filterTimeFrom && hhmm < filterTimeFrom) continue;
      if (filterTimeTo && hhmm > filterTimeTo) continue;

      const last = rawSessions[rawSessions.length - 1];
      if (!last || (item.timestamp - last.maxTs) > BATCH_GAP_MS) {
        rawSessions.push({
          sessionKey: `${dayKey}-${item.timestamp}`,
          dayKey,
          itemsWithTracking: item.trackingNumber && item.trackingNumber.length >= 4 ? [item] : [],
          itemsWithoutTracking: !(item.trackingNumber && item.trackingNumber.length >= 4) ? [item] : [],
          minTs: item.timestamp,
          maxTs: item.timestamp,
        });
      } else {
        last.maxTs = Math.max(last.maxTs, item.timestamp);
        if (item.trackingNumber && item.trackingNumber.length >= 4) {
          last.itemsWithTracking.push(item);
        } else {
          last.itemsWithoutTracking.push(item);
        }
      }
    }

    return rawSessions.sort((a, b) => b.maxTs - a.maxTs);
  }, [allePakete, idbChronik, liste, historyList, filterDate, filterTimeFrom, filterTimeTo]);

  const handleOpenDailyGroup = (items) => {
    const count = openBatchFromPackages(items);
    if (count > 0) {
      const fresh = listBatch();
      setListe(fresh);
      setHistoryList(listBatchHistory());
      setPosState(0);
      setShowHistory(false);
      setUebersicht(false);
    }
  };

  const handleCompleteActiveBatch = () => {
    const count = liste.length;
    if (!count) return;
    completeCurrentBatch();
    setListe(listBatch());
    setHistoryList(listBatchHistory());
    setExpectedCountState(0);
    setShowHistory(true);
    setUebersicht(false);
    if (window.showToast) {
      window.showToast(
        trDyn(lang, 'd12', { count, time: uhr(Date.now()) }),
        'success',
        4000
      );
    }
  };

  const handleToggleHistoryCompleted = (histId, nextDone) => {
    markHistoryBatchCompleted(histId, nextDone);
    setHistoryList(listBatchHistory());
    if (window.showToast && nextDone) {
      window.showToast(
        trDyn(lang, 'd13', { time: uhr(Date.now()) }),
        'success',
        3000
      );
    }
  };

  const handleMarkDailyGroupCompleted = (items) => {
    markPackageGroupCompleted(items);
    setHistoryList(listBatchHistory());
    if (window.showToast) {
      window.showToast(
        trDyn(lang, 'd14', { count: items.length, time: uhr(Date.now()) }),
        'success',
        3000
      );
    }
  };

  const handleMergeHistoryIntoActive = (histId) => {
    if (mergeHistoryIntoActive(histId)) {
      const fresh = listBatch();
      setListe(fresh);
      setHistoryList(listBatchHistory());
      setPosState(Math.min(getBatchPos(), Math.max(0, fresh.length - 1)));
      setShowHistory(false);
      setUebersicht(false);
      if (window.showToast) {
        window.showToast(
          trDyn(lang, 'd15', { count: fresh.length }),
          'info',
          3500
        );
      }
    }
  };

  const handleSaveExpected = () => {
    const n = parseInt(expectedInputVal, 10);
    if (Number.isFinite(n) && n > 0) {
      setBatchExpectedCount(n);
      setExpectedCountState(n);
      try {
        const remaining = Math.max(0, n - liste.length);
        if (remaining > 0) localStorage.setItem('packageCounter', String(remaining));
        else localStorage.removeItem('packageCounter');
      } catch { /* */ }
    } else {
      setBatchExpectedCount(0);
      setExpectedCountState(0);
    }
    setShowExpectedInput(false);
  };

  const handleDisableBatchFeature = async () => {
    await disableBatchBarcodesFeature();
    if (window.showToast) {
      window.showToast(
        tr(
          '📋 Barcode-Stapel deaktiviert. Du kannst ihn jederzeit über das Setup (Einstellungen → Setup starten) wieder einschalten.',
          '📋 Barcode batch disabled. You can re-enable it anytime in Setup (Settings → Start Setup).',
          '📋 Barcodestapel uitgeschakeld. Je kunt deze op elk moment weer inschakelen via de setup (Instellingen → Setup starten).'
        ),
        'info',
        4500
      );
    }
    if (onClose) onClose();
  };

  const carrierGroups = useMemo(() => groupBatchByCarrier(liste), [liste]);

  // Wenn ein ausgewählter Paketdienst nicht mehr im Stapel existiert, auf "Alle" zurücksetzen
  useEffect(() => {
    if (!selectedCarrier) return;
    const targetKey = selectedCarrier === '__other__' ? '' : selectedCarrier;
    const exists = carrierGroups.some((g) => (g.dienst || '').toLowerCase() === targetKey.toLowerCase());
    if (!exists) setSelectedCarrier('');
  }, [carrierGroups, selectedCarrier]);

  const handleSelectCarrier = useCallback((carrierKey) => {
    setSelectedCarrier(carrierKey);
    if (!carrierKey) return;
    const target = carrierKey === '__other__' ? '' : carrierKey;
    const firstOpen = liste.findIndex((x) => !x.scanned && erkenneStapelDienst(x).toLowerCase() === target.toLowerCase());
    const firstAny = liste.findIndex((x) => erkenneStapelDienst(x).toLowerCase() === target.toLowerCase());
    const jump = firstOpen >= 0 ? firstOpen : firstAny;
    if (jump >= 0) setPos(jump);
  }, [liste, setPos]);

  const handleCompleteCarrierBatch = (dienstKey) => {
    const target = dienstKey === '__other__' ? '' : dienstKey;
    const group = carrierGroups.find((g) => (g.dienst || '').toLowerCase() === target.toLowerCase());
    const count = group ? group.count : 0;
    if (!count) return;
    const label = target || tr('Sonstige', 'Other', 'Overige');
    completeBatchByCarrier(target);
    const fresh = listBatch();
    setListe(fresh);
    setHistoryList(listBatchHistory());
    setExpectedCountState(getBatchExpectedCount());
    setSelectedCarrier('');
    setPosState(0);
    if (!fresh.length) {
      setExpectedCountState(0);
      setShowHistory(true);
      setUebersicht(false);
    }
    if (window.showToast) {
      window.showToast(
        `✅ ${label} (${count} ${pkgWord(count)}) ${tr('als erledigt markiert', 'marked as done', 'als afgerond gemarkeerd')} (${uhr(Date.now())})`,
        'success',
        3500
      );
    }
  };

  const handleToggleCarrierAllowed = (dienst, enabled) => {
    const nextDisabled = toggleBatchCarrierEnabled(dienst, enabled);
    setDisabledCarriersState(nextDisabled);
    const fresh = listBatch();
    setListe(fresh);
    setHistoryList(listBatchHistory());
    setPosState((cur) => Math.min(cur, Math.max(0, fresh.length - 1)));
  };

  useEffect(() => {
    if (pos >= liste.length && liste.length > 0) {
      setPos(Math.max(0, liste.length - 1));
    }
  }, [liste.length, pos, setPos]);

  const activeCarrierTarget = selectedCarrier === '__other__' ? '' : selectedCarrier;
  const activeCarrierGroup = useMemo(() => {
    if (!selectedCarrier) return null;
    return carrierGroups.find((g) => (g.dienst || '').toLowerCase() === activeCarrierTarget.toLowerCase()) || null;
  }, [selectedCarrier, carrierGroups, activeCarrierTarget]);

  const carrierSliceIndices = useMemo(() => {
    if (!selectedCarrier) return liste.map((_, i) => i);
    const indices = [];
    liste.forEach((it, idx) => {
      if (erkenneStapelDienst(it).toLowerCase() === activeCarrierTarget.toLowerCase()) {
        indices.push(idx);
      }
    });
    return indices.length > 0 ? indices : liste.map((_, i) => i);
  }, [liste, selectedCarrier, activeCarrierTarget]);

  const posInSlice = useMemo(() => {
    const found = carrierSliceIndices.indexOf(pos);
    return found >= 0 ? found : 0;
  }, [carrierSliceIndices, pos]);

  const effectiveGlobalPos = carrierSliceIndices[posInSlice] ?? pos;
  const e = liste[effectiveGlobalPos] || liste[pos];
  currentItemIdRef.current = e?.id || null;
  const currentDienst = e ? erkenneStapelDienst(e) : '';
  const currentDienstStil = getStapelDienstStil(currentDienst, dark);
  const idx = e ? (wahl[e.id] ?? e.best ?? 0) : 0;
  const code = e ? e.codes[idx] : null;
  const scannedCount = liste.filter((x) => x.scanned).length;
  const allScanned = liste.length > 0 && scannedCount === liste.length;
  const prevGlobalPos = posInSlice > 0 ? carrierSliceIndices[posInSlice - 1] : (effectiveGlobalPos > 0 ? effectiveGlobalPos - 1 : -1);
  const prevItem = prevGlobalPos >= 0 ? liste[prevGlobalPos] : null;
  const prevDienst = prevItem ? erkenneStapelDienst(prevItem) : '';
  const carrierSwitchedHere = !selectedCarrier && prevItem && prevDienst !== currentDienst;
  const activeTimes = liste.map((x) => x.at).filter(Boolean);
  const activeStartTs = activeTimes.length ? Math.min(...activeTimes) : 0;
  const activeEndTs = activeTimes.length ? Math.max(...activeTimes) : 0;
  const activeTimeInfo = activeStartTs ? formatDayAndRange(activeStartTs, activeEndTs, lang) : null;

  // Prüfen, ob vor < 90 Min. am selben Tag ein weiterer Stapel lag (z. B. nach kurzer Unterbrechung durch Kunden)
  const recentMergeCandidate = useMemo(() => {
    if (!liste.length || !historyList.length || !activeStartTs) return null;
    const latestHist = historyList[0];
    if (!latestHist || !latestHist.finishedAt) return null;
    const gap = activeStartTs - latestHist.finishedAt;
    const sameDay = new Date(activeStartTs).toDateString() === new Date(latestHist.finishedAt).toDateString();
    if (sameDay && gap >= 0 && gap <= 90 * 60 * 1000) {
      return latestHist;
    }
    return null;
  }, [liste.length, historyList, activeStartTs]);

  // Weiter zum nächsten Paket (markiert aktuelles mit Uhrzeit als gescannt; bleibt bei aktivem Dienst-Filter innerhalb dieses Dienstes)
  const weiterZumNaechsten = useCallback(() => {
    setListe((curList) => {
      if (!curList.length) return curList;
      setPosState((curPos) => {
        let activeIdx = curPos;
        if (selectedCarrier) {
          const target = selectedCarrier === '__other__' ? '' : selectedCarrier;
          const curMatches = curList[curPos] && erkenneStapelDienst(curList[curPos]).toLowerCase() === target.toLowerCase();
          if (!curMatches) {
            const firstInCarrier = curList.findIndex((it) => erkenneStapelDienst(it).toLowerCase() === target.toLowerCase());
            if (firstInCarrier >= 0) activeIdx = firstInCarrier;
          }
        }
        const curItem = curList[activeIdx];
        if (curItem) markBatchItemScanned(curItem.id, true);
        if (selectedCarrier) {
          const target = selectedCarrier === '__other__' ? '' : selectedCarrier;
          const nextInCarrier = curList.findIndex((it, i) => i > activeIdx && erkenneStapelDienst(it).toLowerCase() === target.toLowerCase());
          if (nextInCarrier >= 0) {
            setBatchPos(nextInCarrier);
            return nextInCarrier;
          }
          setBatchPos(activeIdx);
          return activeIdx;
        }
        if (activeIdx < curList.length - 1) {
          const nextPos = activeIdx + 1;
          setBatchPos(nextPos);
          return nextPos;
        }
        return activeIdx;
      });
      return listBatch();
    });
  }, [selectedCarrier]);

  // 🎙️ Mikrofon-Piep-Detektor starten/stoppen
  const stopMic = useCallback(() => {
    if (rafRef.current) { clearInterval(rafRef.current); rafRef.current = null; }
    if (streamRef.current) {
      try { streamRef.current.getTracks().forEach((tr) => tr.stop()); } catch { /* */ }
      streamRef.current = null;
    }
    if (audioCtxRef.current) {
      try { audioCtxRef.current.close(); } catch { /* */ }
      audioCtxRef.current = null;
    }
    hitFramesRef.current = 0;
  }, []);

  const toggleMic = async () => {
    setMicError('');
    if (micOn) {
      stopMic();
      setMicOn(false);
      return;
    }
    try {
      // 1) Auf Android zuerst die native App-Berechtigung aktiv anfragen
      try {
        await nativeMicPermission();
      } catch { /* */ }

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        setMicError(t('batch.micUnsupported') || 'Mikrofon wird auf diesem Gerät nicht unterstützt');
        return;
      }

      // 2) Audio-Stream anfordern (mit Fallback auf { audio: true }, falls Roh-Audio-Constraints abgelehnt werden)
      let stream = null;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          audio: {
            echoCancellation: false,
            noiseSuppression: false,
            autoGainControl: false,
          },
        });
      } catch {
        stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      }

      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      const ctx = new AudioCtx();
      if (ctx.state === 'suspended') {
        try { await ctx.resume(); } catch { /* */ }
      }
      const source = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.2;
      source.connect(analyser);

      streamRef.current = stream;
      audioCtxRef.current = ctx;
      setMicOn(true);

      const bins = new Uint8Array(analyser.frequencyBinCount);
      const sampleRate = ctx.sampleRate || 48000;
      const binHz = sampleRate / analyser.fftSize;
      // Handscanner / DHL-MDE Piepton liegt typischerweise zwischen 1850 Hz und 3600 Hz
      const minBin = Math.max(1, Math.floor(1850 / binHz));
      const maxBin = Math.min(bins.length - 2, Math.ceil(3600 / binHz));
      // Tieffrequenter Sprach-Bereich (150–900 Hz) zum Abgleich gegen lautes Sprechen
      const speechMinBin = Math.max(1, Math.floor(150 / binHz));
      const speechMaxBin = Math.min(bins.length - 2, Math.ceil(900 / binHz));

      rafRef.current = setInterval(() => {
        analyser.getByteFrequencyData(bins);
        let peakVal = 0;
        let peakBin = minBin;
        let sumHigh = 0;
        for (let i = minBin; i <= maxBin; i++) {
          const v = bins[i];
          sumHigh += v;
          if (v > peakVal) { peakVal = v; peakBin = i; }
        }
        const avgHigh = sumHigh / Math.max(1, maxBin - minBin + 1);

        // Sprach-Energie prüfen
        let sumSpeech = 0;
        for (let i = speechMinBin; i <= speechMaxBin; i++) sumSpeech += bins[i];
        const avgSpeech = sumSpeech / Math.max(1, speechMaxBin - speechMinBin + 1);

        // Schmalbandigkeit: Energie direkt um den Peak (±2 Bins) vs. Rest des Hochtonbands
        let nearSum = 0;
        let nearCount = 0;
        for (let i = Math.max(minBin, peakBin - 2); i <= Math.min(maxBin, peakBin + 2); i++) {
          nearSum += bins[i];
          nearCount++;
        }
        const outerCount = Math.max(1, (maxBin - minBin + 1) - nearCount);
        const outerAvg = Math.max(1, (sumHigh - nearSum) / outerCount);

        const isPureBeep = peakVal >= 155 && (peakVal / outerAvg) >= 1.85 && peakVal > avgSpeech * 1.15 && avgHigh < peakVal * 0.65;

        if (isPureBeep) {
          hitFramesRef.current += 1;
          const now = Date.now();
          if (hitFramesRef.current >= 2 && now - lastBeepTsRef.current > 1200) {
            lastBeepTsRef.current = now;
            hitFramesRef.current = 0;
            setBeepFlash(true);
            setTimeout(() => setBeepFlash(false), 420);
            weiterZumNaechsten();
          }
        } else {
          hitFramesRef.current = 0;
        }
      }, 35);
    } catch (err) {
      setMicError(
        t('batch.micDenied') ||
        'Mikrofon-Zugriff wurde blockiert. Bitte Mikrofon-Berechtigung für PaketPilot erlauben.'
      );
      stopMic();
      setMicOn(false);
    }
  };

  useEffect(() => () => stopMic(), [stopMic]);

  // 🖼️ Originalfoto für das aktuelle Paket suchen und öffnen
  const findePaketIdFuerEintrag = (entry) => {
    if (!entry) return null;
    if (entry.paketId) return entry.paketId;
    const trk = String(entry.tracking || '').trim().toUpperCase();
    const match = (pakete || []).find((p) => {
      if (!p) return false;
      const pTrk = String(p.trackingNumber || p.trackingNummer || '').trim().toUpperCase();
      if (trk && pTrk && (pTrk === trk || pTrk.includes(trk) || trk.includes(pTrk))) return true;
      if (entry.name && p.name && String(p.name).trim().toLowerCase() === String(entry.name).trim().toLowerCase()) {
        if (!entry.regalplatz || String(p.regalplatz || '').toUpperCase() === String(entry.regalplatz).toUpperCase()) return true;
      }
      return false;
    });
    return match?.id || entry.imageId || null;
  };

  const oeffneOriginalfoto = async () => {
    if (!e) return;
    setFotoError('');
    const targetId = findePaketIdFuerEintrag(e);
    if (!targetId) {
      setFotoError(t('batch.noPhotoFound') || 'Kein Originalfoto für dieses Paket gefunden.');
      return;
    }
    setFotoLoading(true);
    try {
      const url = await ladePaketVollbild(targetId);
      setZoomSrc(url);
    } catch {
      setFotoError(t('batch.noPhotoFound') || 'Kein Originalfoto für dieses Paket gefunden.');
    } finally {
      setFotoLoading(false);
    }
  };

  const schliesseFoto = () => {
    if (zoomSrc) {
      try { URL.revokeObjectURL(zoomSrc); } catch { /* */ }
    }
    setZoomSrc(null);
  };

  const btn = (primary, extra = {}) => ({
    padding: '14px 10px', borderRadius: 10, fontSize: 16, fontWeight: 700, cursor: 'pointer',
    border: primary ? 'none' : `1px solid ${dark ? '#444' : '#ccc'}`,
    background: primary ? '#1976d2' : (dark ? '#2d333b' : '#fff'), color: primary ? '#fff' : (dark ? '#fff' : '#23272f'),
    ...extra,
  });

  // Gefilterte 7-Tage-Historie nach Tag & Uhrzeit
  const filteredHistory = historyList.filter((h) => {
    if (!h || !h.finishedAt) return false;
    const d = new Date(h.finishedAt);
    if (filterDate) {
      const yyyy = d.getFullYear();
      const mm = String(d.getMonth() + 1).padStart(2, '0');
      const dd = String(d.getDate()).padStart(2, '0');
      if (`${yyyy}-${mm}-${dd}` !== filterDate) return false;
    }
    const hhmm = `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
    if (filterTimeFrom && hhmm < filterTimeFrom) return false;
    if (filterTimeTo && hhmm > filterTimeTo) return false;
    return true;
  });

  const handleRestore = (histId) => {
    if (restoreBatch(histId)) {
      const fresh = listBatch();
      setListe(fresh);
      setHistoryList(listBatchHistory());
      setPosState(Math.min(getBatchPos(), Math.max(0, fresh.length - 1)));
      setShowHistory(false);
      setUebersicht(false);
    }
  };

  const toggleCurrentScanned = () => {
    if (!e) return;
    markBatchItemScanned(e.id, !e.scanned);
    setListe(listBatch());
  };

  const totalHistoryCount = filteredHistory.length + dailyPackageGroups.length;
  const selectableCarriers = ['DHL', 'DPD', 'GLS', 'Hermes', 'UPS', 'PostNL', 'Mondial Relay', 'InPost', 'Vinted', 'Sonstige'];
  const enabledSelectableCount = selectableCarriers.filter((c) => !disabledCarriers.some((d) => d.toLowerCase() === c.toLowerCase())).length;

  const renderCarrierSettingsPanel = () => (
    <div
      data-testid="batch-carrier-settings-panel"
      style={{
        padding: '10px 12px',
        borderRadius: 10,
        background: dark ? '#0f172a' : '#f8fafc',
        border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
        fontSize: 12.5,
        textAlign: 'left',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
      }}
    >
      <div style={{ fontWeight: 800, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
        <span>
          ⚙️ {tr(
            'Welche Paketdienste sollen im Barcode-Stapel gesammelt werden? (optional)',
            'Which carriers should be collected in the barcode batch? (optional)',
            'Welke pakketdiensten moeten in de barcodestapel worden verzameld? (optioneel)'
          )}
        </span>
        <button
          type="button"
          onClick={() => setShowCarrierSettings(false)}
          style={{ background: 'none', border: 'none', color: 'inherit', cursor: 'pointer', fontWeight: 800, fontSize: 13 }}
        >
          ✕
        </button>
      </div>
      <div style={{ fontSize: 11.5, opacity: 0.82, lineHeight: 1.35 }}>
        {tr(
          'Deaktiviere hier Paketdienste, die du nicht vom Bildschirm abscannst (z. B. wenn du den Stapel nur für DHL oder DPD nutzt). Abgewählte Dienste erzeugen oben keinen Stapel mehr.',
          'Uncheck carriers that you do not scan off the screen (e.g. if you only use the batch for DHL or DPD). Unchecked carriers will no longer appear in the batch.',
          'Schakel hier pakketdiensten uit die je niet vanaf het scherm scant (bijv. als je de stapel alleen voor DHL of DPD gebruikt). Uitgeschakelde diensten verschijnen niet meer in de stapel.'
        )}
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
        <button
          type="button"
          data-testid="batch-preset-only-dhl"
          onClick={() => {
            const next = setBatchDisabledCarriers([...STAPEL_DIENSTE_REIHENFOLGE, 'Sonstige'].filter((c) => c !== 'DHL'), true);
            setDisabledCarriersState(next);
            const fresh = listBatch();
            setListe(fresh);
            setHistoryList(listBatchHistory());
            setPosState(0);
          }}
          style={{
            padding: '5px 9px', borderRadius: 7, fontSize: 11.5, fontWeight: 800, cursor: 'pointer',
            border: '1px solid #eab308', background: dark ? '#422006' : '#fef9c3', color: dark ? '#fde047' : '#a16207',
          }}
        >
          🟡 {tr('Nur DHL', 'Only DHL', 'Alleen DHL')}
        </button>
        <button
          type="button"
          data-testid="batch-preset-dhl-dpd"
          onClick={() => {
            const next = setBatchDisabledCarriers([...STAPEL_DIENSTE_REIHENFOLGE, 'Sonstige'].filter((c) => c !== 'DHL' && c !== 'DPD'), true);
            setDisabledCarriersState(next);
            const fresh = listBatch();
            setListe(fresh);
            setHistoryList(listBatchHistory());
            setPosState(0);
          }}
          style={{
            padding: '5px 9px', borderRadius: 7, fontSize: 11.5, fontWeight: 800, cursor: 'pointer',
            border: '1px solid #ef4444', background: dark ? '#450a0a' : '#fee2e2', color: dark ? '#fca5a5' : '#b91c1c',
          }}
        >
          🟡🔴 {tr('Nur DHL & DPD', 'Only DHL & DPD', 'Alleen DHL & DPD')}
        </button>
        <button
          type="button"
          data-testid="batch-preset-all-carriers"
          onClick={() => {
            const next = setBatchDisabledCarriers([], false);
            setDisabledCarriersState(next);
          }}
          style={{
            padding: '5px 9px', borderRadius: 7, fontSize: 11.5, fontWeight: 700, cursor: 'pointer',
            border: `1px solid ${dark ? '#475569' : '#94a3b8'}`, background: dark ? '#1e293b' : '#ffffff', color: 'inherit',
          }}
        >
          ✓ {tr('Alle Dienste aktiv', 'All carriers active', 'Alle diensten actief')}
        </button>
      </div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 2 }}>
        {selectableCarriers.map((cName) => {
          const enabled = !disabledCarriers.some((d) => d.toLowerCase() === cName.toLowerCase());
          const st = getStapelDienstStil(cName === 'Sonstige' ? '' : cName, dark);
          const displayLabel = cName === 'Sonstige' ? tr('Sonstige', 'Other', 'Overige') : cName;
          return (
            <button
              key={cName}
              type="button"
              data-testid={`batch-carrier-toggle-${cName}`}
              onClick={() => handleToggleCarrierAllowed(cName, !enabled)}
              style={{
                padding: '5px 10px',
                borderRadius: 999,
                fontSize: 12,
                fontWeight: 700,
                cursor: 'pointer',
                border: `1.5px solid ${enabled ? st.border : (dark ? '#334155' : '#cbd5e1')}`,
                background: enabled ? st.bg : 'transparent',
                color: enabled ? st.text : (dark ? '#64748b' : '#94a3b8'),
                opacity: enabled ? 1 : 0.65,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              <span>{enabled ? '☑️' : '⬜'}</span>
              <span>{st.icon} {displayLabel}</span>
            </button>
          );
        })}
      </div>
    </div>
  );

  return (
    <div
      data-testid="batch-view"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 7700,
        background: beepFlash ? '#16a34a' : (dark ? '#15181d' : '#f4f6f8'),
        color: beepFlash ? '#fff' : (dark ? '#fff' : '#23272f'),
        transition: 'background-color 0.15s ease',
        display: 'flex',
        flexDirection: 'column',
        padding: '12px 14px calc(env(safe-area-inset-bottom, 0px) + 58px) 14px',
        overflow: 'auto',
        boxSizing: 'border-box',
      }}
    >
      {/* ── Kopfzeile mit Titel, How-To-Video, Mikrofon-Schalter & Schließen ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <strong style={{ fontSize: 17 }}>
          📋 {t('batch.title')} {liste.length > 0 ? `(${scannedCount}/${liste.length})` : ''}
        </strong>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <button
            type="button"
            data-testid="batch-video-toggle"
            onClick={() => setShowVideo((v) => !v)}
            style={{
              padding: '6px 10px',
              borderRadius: 999,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
              border: `1px solid ${showVideo ? '#38bdf8' : (dark ? '#444' : '#ccc')}`,
              background: showVideo ? '#0284c7' : (dark ? '#2d333b' : '#fff'),
              color: showVideo ? '#fff' : 'inherit',
            }}
          >
            🎬 {t('batch.howToVideo') || 'How-To'}
          </button>
          {liste.length > 0 && !showHistory && (
            <button
              type="button"
              data-testid="batch-mic-toggle"
              onClick={toggleMic}
              title={t('batch.micHint') || 'Erkennt das Piepen des DHL-Scanners und schaltet automatisch weiter'}
              style={{
                padding: '6px 11px',
                borderRadius: 999,
                fontSize: 12.5,
                fontWeight: 700,
                cursor: 'pointer',
                border: micOn ? '2px solid #22c55e' : `1px solid ${dark ? '#444' : '#ccc'}`,
                background: micOn ? '#16a34a' : (dark ? '#2d333b' : '#fff'),
                color: micOn ? '#fff' : 'inherit',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 5,
              }}
            >
              {micOn ? `🎙️ ${t('batch.micOn') || 'Piep AN'}` : `🔇 ${t('batch.micOff') || 'Piep AUS'}`}
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            style={{
              padding: '6px 11px',
              borderRadius: 999,
              fontSize: 12.5,
              fontWeight: 700,
              cursor: 'pointer',
              border: `1px solid ${dark ? '#444' : '#ccc'}`,
              background: dark ? '#2d333b' : '#fff',
              color: 'inherit',
            }}
          >
            ✕
          </button>
        </div>
      </div>

      {/* ── Klare 3-Reiter-Navigation oben: Aktueller Stapel | Paketliste | Alle Stapel (7 Tage) ── */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: liste.length > 0 ? '1fr 1fr 1fr' : '1fr 1fr',
          gap: 6,
          padding: 4,
          borderRadius: 10,
          background: dark ? '#23272f' : '#e2e8f0',
          marginBottom: 10,
        }}
      >
        <button
          type="button"
          onClick={() => { setShowHistory(false); setUebersicht(false); }}
          style={{
            padding: '8px 6px',
            borderRadius: 8,
            border: 'none',
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
            background: !showHistory && !uebersicht ? '#1976d2' : 'transparent',
            color: !showHistory && !uebersicht ? '#fff' : 'inherit',
          }}
        >
          📲 {tr('Aktueller Stapel', 'Current batch', 'Huidige stapel')} {liste.length > 0 ? `(${scannedCount}/${liste.length})` : ''}
        </button>
        {liste.length > 0 && (
          <button
            type="button"
            onClick={() => { setShowHistory(false); setUebersicht(true); }}
            style={{
              padding: '8px 6px',
              borderRadius: 8,
              border: 'none',
              fontSize: 13,
              fontWeight: 800,
              cursor: 'pointer',
              background: !showHistory && uebersicht ? '#1976d2' : 'transparent',
              color: !showHistory && uebersicht ? '#fff' : 'inherit',
            }}
          >
            ☰ {tr('Paketliste', 'Parcel list', 'Pakketlijst')} ({liste.length})
          </button>
        )}
        <button
          type="button"
          data-testid="batch-history-toggle"
          onClick={() => { setShowHistory(true); setUebersicht(false); }}
          style={{
            padding: '8px 6px',
            borderRadius: 8,
            border: 'none',
            fontSize: 13,
            fontWeight: 800,
            cursor: 'pointer',
            background: showHistory ? '#1976d2' : 'transparent',
            color: showHistory ? '#fff' : 'inherit',
          }}
        >
          🗂️ {tr('Alle Stapel', 'All batches', 'Alle stapels')} ({totalHistoryCount + (liste.length > 0 ? 1 : 0)})
        </button>
      </div>

      {showVideo && (
        <div style={{ maxWidth: 560, width: '100%', margin: '0 auto 10px' }}>
          <BatchTutorialVideo compact />
        </div>
      )}

      {micError && (
        <div
          style={{
            fontSize: 13,
            color: '#fff',
            background: '#dc2626',
            borderRadius: 10,
            padding: '10px 12px',
            textAlign: 'center',
            marginBottom: 8,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
            alignItems: 'center',
          }}
        >
          <div>⚠️ {micError}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', justifyContent: 'center' }}>
            <button
              type="button"
              onClick={toggleMic}
              style={{
                padding: '6px 12px',
                borderRadius: 8,
                border: '1px solid rgba(255,255,255,0.5)',
                background: 'rgba(255,255,255,0.18)',
                color: '#fff',
                fontWeight: 700,
                fontSize: 12,
                cursor: 'pointer',
              }}
            >
              🔄 {t('batch.micRetry') || tr('Berechtigung erneut anfragen', 'Request permission again', 'Toestemming opnieuw vragen')}
            </button>
            {nativeCamSupported() && (
              <button
                type="button"
                onClick={() => nativeOpenBatteryOpt()}
                style={{
                  padding: '6px 12px',
                  borderRadius: 8,
                  border: 'none',
                  background: '#fff',
                  color: '#dc2626',
                  fontWeight: 700,
                  fontSize: 12,
                  cursor: 'pointer',
                }}
              >
                ⚙️ {t('batch.openAppSettings') || tr('App-Berechtigungen öffnen', 'Open app permissions', 'App-machtigingen openen')}
              </button>
            )}
          </div>
        </div>
      )}

      {beepFlash && (
        <div style={{
          textAlign: 'center', fontWeight: 800, fontSize: 18, padding: '6px 12px',
          background: 'rgba(255,255,255,0.2)', borderRadius: 8, marginBottom: 8,
        }}>
          ✓ {t('batch.beepDetected') || tr('Piep erkannt → Nächstes Paket!', 'Beep detected → Next parcel!', 'Piep herkend → Volgend pakket!')}
        </div>
      )}

      {/* ── Stapel-Kopfbox für den aktuellen Stapel: Uhrzeit-Spanne, Boten-Zähler (Abgleich) & „Ganzen Stapel erledigt" ── */}
      {liste.length > 0 && !showHistory && (
        <div
          style={{
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: 8,
            background: dark ? '#1e242c' : '#ffffff',
            border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 8,
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14 }}>
                🕐 {activeTimeInfo ? activeTimeInfo.fullLabel : tr('Aktuelle Lieferung', 'Current delivery', 'Huidige levering')} · {liste.length} {pkgWord(liste.length)}
              </div>
              <div style={{ fontSize: 11.5, opacity: 0.72 }}>
                {tr(
                  'Automatisch nach Paketdienst sortiert (z. B. erst alle DHL, dann DPD) · Lieferungen > 15 Min. getrennt',
                  'Automatically sorted by carrier (e.g. all DHL first, then DPD) · Deliveries > 15 min separated',
                  'Automatisch gesorteerd op pakketdienst (bijv. eerst alle DHL, daarna DPD) · Leveringen > 15 min. gescheiden'
                )}
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
              <button
                type="button"
                data-testid="batch-carrier-settings-toggle"
                onClick={() => setShowCarrierSettings((s) => !s)}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: `1px solid ${showCarrierSettings || disabledCarriers.length > 0 ? '#7b61ff' : (dark ? '#475569' : '#94a3b8')}`,
                  background: showCarrierSettings || disabledCarriers.length > 0 ? (dark ? '#241f3a' : '#f3f0ff') : (dark ? '#2d333b' : '#f8fafc'),
                  color: 'inherit',
                }}
              >
                ⚙️ {tr('Paketdienste', 'Carriers', 'Pakketdiensten')}
                {enabledSelectableCount < selectableCarriers.length ? ` (${enabledSelectableCount}/${selectableCarriers.length})` : ''}
              </button>
              <button
                type="button"
                data-testid="batch-courier-counter-btn"
                onClick={() => {
                  setExpectedInputVal(expectedCount > 0 ? String(expectedCount) : '');
                  setShowExpectedInput((s) => !s);
                }}
                style={{
                  padding: '6px 10px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  border: `1px solid ${expectedCount > 0 ? '#0284c7' : (dark ? '#475569' : '#94a3b8')}`,
                  background: expectedCount > 0 ? (dark ? '#0c4a6e' : '#e0f2fe') : (dark ? '#2d333b' : '#f8fafc'),
                  color: 'inherit',
                }}
              >
                🚚 {expectedCount > 0
                  ? trDyn(lang, 'd16', { count: expectedCount })
                  : tr('Boten-Anzahl (optional)', 'Courier count (optional)', 'Aantal bezorger (optioneel)')}
              </button>
              <button
                type="button"
                data-testid="batch-complete-all-btn"
                onClick={handleCompleteActiveBatch}
                style={{
                  padding: '6px 11px',
                  borderRadius: 8,
                  fontSize: 12,
                  fontWeight: 800,
                  cursor: 'pointer',
                  border: '1px solid #22c55e',
                  background: dark ? '#14532d' : '#dcfce7',
                  color: dark ? '#86efac' : '#166534',
                }}
              >
                ✅ {tr('Ganzen Stapel als erledigt markieren', 'Mark entire batch as done', 'Hele stapel als afgerond markeren')}
              </button>
            </div>
          </div>

          {/* 🏷️ Paketdienst-Filterleiste: Alle (nach Dienst sortiert) | 🟡 DHL | 🔴 DPD | 🔵 GLS … */}
          {carrierGroups.length > 0 && (
            <div
              data-testid="batch-carrier-bar"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
                paddingTop: 4,
                borderTop: `1px solid ${dark ? '#2d3542' : '#e2e8f0'}`,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  data-testid="batch-carrier-pill-all"
                  onClick={() => setSelectedCarrier('')}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 999,
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: 'pointer',
                    border: !selectedCarrier ? '2px solid #7b61ff' : `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                    background: !selectedCarrier ? '#7b61ff' : (dark ? '#2d333b' : '#f1f5f9'),
                    color: !selectedCarrier ? '#fff' : 'inherit',
                  }}
                >
                  📋 {tr('Alle sortiert', 'All sorted', 'Alle gesorteerd')} ({scannedCount}/{liste.length})
                </button>
                {carrierGroups.map((g) => {
                  const key = g.dienst || '__other__';
                  const isSel = selectedCarrier.toLowerCase() === key.toLowerCase();
                  const st = getStapelDienstStil(g.dienst, dark);
                  const label = g.dienst || tr('Sonstige', 'Other', 'Overige');
                  return (
                    <button
                      key={key}
                      type="button"
                      data-testid={`batch-carrier-pill-${g.dienst || 'other'}`}
                      onClick={() => handleSelectCarrier(isSel ? '' : key)}
                      style={{
                        padding: '5px 11px',
                        borderRadius: 999,
                        fontSize: 12.5,
                        fontWeight: 800,
                        cursor: 'pointer',
                        border: isSel ? `2px solid ${st.border}` : `1px solid ${g.allScanned ? '#22c55e' : st.border}`,
                        background: isSel ? st.activeBg : (g.allScanned ? (dark ? '#14532d' : '#dcfce7') : st.bg),
                        color: isSel ? st.activeText : (g.allScanned ? (dark ? '#86efac' : '#166534') : st.text),
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 5,
                      }}
                    >
                      <span>{g.allScanned ? '✅' : st.icon}</span>
                      <span>{label} ({g.scannedCount}/{g.count})</span>
                    </button>
                  );
                })}
              </div>

              {/* Gezielte Aktion für den aktuell gewählten Paketdienst (bzw. bei mehreren Diensten im Stapel) */}
              {(activeCarrierGroup || carrierGroups.length > 1) && (() => {
                const targetGroup = activeCarrierGroup || carrierGroups.find((g) => (g.dienst || '') === currentDienst) || carrierGroups[0];
                if (!targetGroup) return null;
                const dKey = targetGroup.dienst || '__other__';
                const dLabel = targetGroup.dienst || tr('Sonstige', 'Other', 'Overige');
                const st = getStapelDienstStil(targetGroup.dienst, dark);
                return (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <button
                      type="button"
                      data-testid={`batch-complete-carrier-${targetGroup.dienst || 'other'}`}
                      onClick={() => handleCompleteCarrierBatch(dKey)}
                      style={{
                        padding: '5px 10px',
                        borderRadius: 8,
                        fontSize: 11.5,
                        fontWeight: 800,
                        cursor: 'pointer',
                        border: `1px solid ${st.border}`,
                        background: st.bg,
                        color: st.text,
                      }}
                    >
                      ✅ {tr('Nur', 'Only', 'Alleen')} {st.icon} {dLabel} ({targetGroup.count}) {tr('erledigt', 'done', 'afgerond')}
                    </button>
                    {targetGroup.dienst && (
                      <button
                        type="button"
                        data-testid={`batch-ignore-carrier-${targetGroup.dienst}`}
                        onClick={() => {
                          handleToggleCarrierAllowed(targetGroup.dienst, false);
                          if (window.showToast) {
                            window.showToast(
                              `🙈 ${targetGroup.dienst} ${tr('wird nicht mehr im Barcode-Stapel gesammelt (unter ⚙️ Paketdienste wieder einschaltbar)', 'will no longer be collected in the batch (can be re-enabled under ⚙️ Carriers)', 'wordt niet meer in de barcodestapel verzameld (onder ⚙️ Pakketdiensten weer in te schakelen)')}`,
                              'info',
                              4000
                            );
                          }
                        }}
                        title={tr(
                          'Diesen Paketdienst aus dem Stapel entfernen und künftig nicht mehr sammeln',
                          'Remove this carrier from the batch and stop collecting it in the future',
                          'Deze pakketdienst uit de stapel verwijderen en in het vervolg niet meer verzamelen'
                        )}
                        style={{
                          padding: '5px 9px',
                          borderRadius: 8,
                          fontSize: 11.5,
                          fontWeight: 700,
                          cursor: 'pointer',
                          border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                          background: 'transparent',
                          color: 'inherit',
                        }}
                      >
                        🙈 {targetGroup.dienst} {tr('nicht nutzen', 'do not use', 'niet gebruiken')}
                      </button>
                    )}
                  </div>
                );
              })()}
            </div>
          )}

          {showCarrierSettings && renderCarrierSettingsPanel()}

          {/* 🚚 Eingabefeld für Boten-Zähler (Soll-Anzahl vom Zusteller) */}
          {showExpectedInput && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
                padding: '8px 10px',
                borderRadius: 8,
                background: dark ? '#0f172a' : '#f1f5f9',
                border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
              }}
            >
              <span style={{ fontSize: 13, fontWeight: 700 }}>
                🚚 {tr('Wie viele Pakete hat der Bote gebracht?', 'How many parcels did the courier bring?', 'Hoeveel pakketten heeft de bezorger gebracht?')}
              </span>
              <input
                type="number"
                min="0"
                max="500"
                value={expectedInputVal}
                onChange={(ev) => setExpectedInputVal(ev.target.value.replace(/[^\d]/g, ''))}
                placeholder={tr('z. B. 30', 'e.g. 30', 'bijv. 30')}
                style={{
                  width: 80,
                  padding: '6px 8px',
                  borderRadius: 6,
                  border: `1px solid ${dark ? '#475569' : '#94a3b8'}`,
                  background: dark ? '#1e293b' : '#fff',
                  color: 'inherit',
                  fontSize: 14,
                  fontWeight: 700,
                }}
                autoFocus
              />
              <button
                type="button"
                onClick={handleSaveExpected}
                style={{
                  padding: '6px 12px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#16a34a',
                  color: '#fff',
                  fontWeight: 700,
                  fontSize: 12.5,
                  cursor: 'pointer',
                }}
              >
                ✓ {tr('Speichern', 'Save', 'Opslaan')}
              </button>
              {expectedCount > 0 && (
                <button
                  type="button"
                  onClick={() => {
                    setBatchExpectedCount(0);
                    setExpectedCountState(0);
                    setShowExpectedInput(false);
                  }}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 6,
                    border: `1px solid ${dark ? '#475569' : '#cbd5e1'}`,
                    background: 'transparent',
                    color: 'inherit',
                    fontSize: 12,
                    cursor: 'pointer',
                  }}
                >
                  {tr('Zurücksetzen', 'Reset', 'Resetten')}
                </button>
              )}
            </div>
          )}

          {/* 📊 Live-Abgleich mit Boten-Zähler */}
          {expectedCount > 0 && (
            <div
              data-testid="batch-courier-reconcile"
              style={{
                padding: '7px 10px',
                borderRadius: 8,
                fontSize: 13,
                fontWeight: 700,
                background: liste.length === expectedCount
                  ? (dark ? 'rgba(22,163,74,0.22)' : '#dcfce7')
                  : liste.length < expectedCount
                    ? (dark ? 'rgba(217,119,6,0.22)' : '#fef3c7')
                    : (dark ? 'rgba(2,132,199,0.2)' : '#e0f2fe'),
                border: `1px solid ${liste.length === expectedCount ? '#22c55e' : liste.length < expectedCount ? '#f59e0b' : '#0284c7'}`,
                color: liste.length === expectedCount
                  ? (dark ? '#86efac' : '#166534')
                  : liste.length < expectedCount
                    ? (dark ? '#fcd34d' : '#92400e')
                    : (dark ? '#7dd3fc' : '#075985'),
              }}
            >
              {liste.length === expectedCount && (
                <span>
                  {trDyn(lang, 'd17', { actual: liste.length, expected: expectedCount })}
                </span>
              )}
              {liste.length < expectedCount && (
                <span>
                  {trDyn(lang, 'd18', { actual: liste.length, expected: expectedCount, missing: expectedCount - liste.length, pkgWord: pkgWord(expectedCount - liste.length) })}
                </span>
              )}
              {liste.length > expectedCount && (
                <span>
                  {trDyn(lang, 'd19', { actual: liste.length, expected: expectedCount })}
                </span>
              )}
            </div>
          )}

          {/* 🔗 Hinweis, falls am selben Tag kurz vorher (< 90 Min.) schon ein Stapel erfasst wurde (z. B. nach kurzer Kunden-Pause) */}
          {recentMergeCandidate && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
                padding: '6px 10px',
                borderRadius: 8,
                fontSize: 12,
                background: dark ? 'rgba(56,189,248,0.12)' : '#f0f9ff',
                border: `1px dashed ${dark ? '#0284c7' : '#7dd3fc'}`,
              }}
            >
              <span>
                {trDyn(lang, 'd20', { timeRange: `${formatUhrzeit(recentMergeCandidate.startedAt, lang)}–${uhr(recentMergeCandidate.finishedAt)}`, prevCount: recentMergeCandidate.count, prevWord: pkgWord(recentMergeCandidate.count), totalCount: liste.length + recentMergeCandidate.count, totalWord: pkgWord(liste.length + recentMergeCandidate.count) })}
              </span>
              <button
                type="button"
                onClick={() => handleMergeHistoryIntoActive(recentMergeCandidate.id)}
                style={{
                  padding: '4px 10px',
                  borderRadius: 6,
                  border: 'none',
                  background: '#0284c7',
                  color: '#fff',
                  fontSize: 12,
                  fontWeight: 700,
                  cursor: 'pointer',
                  whiteSpace: 'nowrap',
                }}
              >
                ➕ {trDyn(lang, 'd21', { count: liste.length + recentMergeCandidate.count })}
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Leer-Zustand (mit direktem Zugriff auf letzte Stapel & Lieferungen der Woche) ── */}
      {!liste.length && !showHistory && (
        <div style={{ padding: 18, textAlign: 'center' }}>
          <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 6 }}>
            ✅ {tr('Aktuell ist kein offener Barcode-Stapel aktiv', 'No open barcode batch is currently active', 'Er is momenteel geen open barcodestapel actief')}
          </div>
          <div style={{ opacity: 0.75, fontSize: 13, marginBottom: 14 }}>
            {tr(
              'Sobald du neue Pakete fotografierst, startet hier automatisch ein frischer Stapel bei Paket 1.',
              'As soon as you photograph new parcels, a fresh batch automatically starts here at parcel 1.',
              'Zodra je nieuwe pakketten fotografeert, start hier automatisch een nieuwe stapel bij pakket 1.'
            )}
          </div>

          {/* 💡 Erklärung & Abschalt-Option */}
          <div style={{
            maxWidth: 460, margin: '0 auto 16px', padding: '12px 14px', borderRadius: 10, textAlign: 'left',
            background: dark ? '#1e293b' : '#eef2ff', border: `1px solid ${dark ? '#334155' : '#c7d2fe'}`,
            fontSize: 13, lineHeight: 1.45,
          }}>
            <div style={{ fontWeight: 700, marginBottom: 4 }}>
              💡 {tr('Wozu ist der Barcode-Stapel da?', 'What is the barcode batch for?', 'Waarvoor dient de barcodestapel?')}
            </div>
            <div style={{ opacity: 0.9 }}>
              {tr(
                'Beim Fotografieren deiner Pakete speichert PaketPilot die echten Etikett-Barcodes im Hintergrund mit. Nach einer Lieferung kannst du hier alle Barcodes nacheinander groß auf dem Display abspielen und bequem am Stück mit dem DHL-/Kurier-Scanner abscannen (z. B. für die DHL-Schnelleinlagerung), ohne jedes Paket erneut in die Hand zu nehmen.',
                'While photographing your parcels, ParcelPilot saves the original label barcodes in the background. After a delivery, you can display all barcodes one by one on screen and scan them in one go with your courier handheld scanner — without touching every parcel again.',
                'Bij het fotograferen van je pakketten slaat ParcelPilot de echte labelbarcodes op de achtergrond op. Na een levering kun je hier alle barcodes één voor één groot op het scherm afspelen en achter elkaar met de koerierscanner scannen — zonder elk pakket opnieuw vast te pakken.'
              )}
            </div>
            <div style={{ marginTop: 10 }}>
              {renderCarrierSettingsPanel()}
            </div>
            <button
              type="button"
              onClick={handleDisableBatchFeature}
              style={{
                marginTop: 10, padding: '7px 12px', borderRadius: 8, fontSize: 12, fontWeight: 700, cursor: 'pointer',
                border: `1px solid ${dark ? '#475569' : '#94a3b8'}`,
                background: dark ? '#0f172a' : '#fff', color: 'inherit', width: '100%',
              }}
            >
              🙈 {tr(
                'Barcode-Stapel nicht mehr anzeigen (kann über Setup wieder geändert werden)',
                'Hide barcode batch (can be re-enabled in Setup)',
                'Barcodestapel niet meer tonen (kan via Setup weer worden gewijzigd)'
              )}
            </button>
          </div>

          {(historyList.length > 0 || dailyPackageGroups.length > 0) && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, maxWidth: 400, margin: '0 auto' }}>
              {historyList.length > 0 && (
                <button
                  type="button"
                  onClick={() => handleRestore(historyList[0].id)}
                  style={btn(true)}
                >
                  ↩️ {tr('Letzten Stapel wieder öffnen', 'Reopen last batch', 'Laatste stapel heropenen')} ({historyList[0].count} {pkgWord(historyList[0].count)} · {formatDayAndRange(historyList[0].startedAt, historyList[0].finishedAt, lang).fullLabel})
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowHistory(true)}
                style={btn(historyList.length === 0)}
              >
                🗂️ {tr('Alle Lieferungen & Stapel der letzten 7 Tage', 'All deliveries & batches of the last 7 days', 'Alle leveringen & stapels van de afgelopen 7 dagen')} ({totalHistoryCount})
              </button>
            </div>
          )}
        </div>
      )}

      {/* ── Hauptansicht: Aktueller Barcode ── */}
      {liste.length > 0 && !uebersicht && !showHistory && e && (
        <>
          {/* 💡 Beim ERSTEN Paket (pos === 0): Erklärung, warum dieses Feld da ist + Einstellung zum Ausblenden */}
          {pos === 0 && (
            <div
              data-testid="batch-first-package-explain"
              style={{
                padding: '10px 12px',
                marginBottom: 8,
                borderRadius: 10,
                background: dark ? 'rgba(123, 97, 255, 0.14)' : '#f3f0ff',
                border: `1px solid ${dark ? '#7b61ff' : '#c4b5fd'}`,
                fontSize: 12.5,
                lineHeight: 1.4,
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 3 }}>
                💡 {tr('Warum gibt es den Barcode-Stapel?', 'Why is there a barcode batch?', 'Waarom is er een barcodestapel?')}
              </div>
              <div style={{ opacity: 0.9 }}>
                {tr(
                  'Beim Erfassen speichert PaketPilot automatisch die Etikett-Barcodes deiner Pakete. Nach einer Kurierlieferung kannst du hier alle Barcodes nacheinander groß auf dem Bildschirm abspielen und direkt am Stück mit dem Zusteller-Scanner (z. B. DHL-Handscanner) abscannen — ohne jedes Paket noch einmal anfassen zu müssen.',
                  'When capturing parcels, ParcelPilot automatically saves their label barcodes. After a courier delivery, you can display all barcodes one by one on screen and scan them in one go with the courier handheld scanner — without touching every parcel again.',
                  'Bij het vastleggen slaat ParcelPilot automatisch de labelbarcodes van je pakketten op. Na een koerierslevering kun je hier alle barcodes één voor één groot op het scherm afspelen en direct achter elkaar met de koerierscanner scannen — zonder elk pakket opnieuw aan te raken.'
                )}
              </div>
              <div style={{ marginTop: 8, display: 'flex', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  data-testid="batch-disable-btn"
                  onClick={handleDisableBatchFeature}
                  style={{
                    padding: '6px 10px',
                    borderRadius: 7,
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                    border: `1px solid ${dark ? '#64748b' : '#94a3b8'}`,
                    background: dark ? '#1e293b' : '#ffffff',
                    color: 'inherit',
                  }}
                >
                  🙈 {tr(
                    'Barcode-Stapel nicht mehr anzeigen (über Setup wieder änderbar)',
                    'Hide barcode batch (can be changed in Setup)',
                    'Barcodestapel niet meer tonen (via Setup weer te wijzigen)'
                  )}
                </button>
              </div>
            </div>
          )}

          {/* 🎉 Hinweis, wenn alle Pakete im Stapel bereits gescannt wurden */}
          {allScanned && (
            <div
              style={{
                background: dark ? 'rgba(22,163,74,0.2)' : '#dcfce7',
                border: '1px solid #22c55e',
                color: dark ? '#86efac' : '#166534',
                borderRadius: 10,
                padding: '8px 12px',
                marginBottom: 8,
                fontSize: 13,
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span>
                🎉 {trDyn(lang, 'd22', { count: liste.length })}
              </span>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={handleCompleteActiveBatch}
                  style={{
                    padding: '5px 10px',
                    borderRadius: 6,
                    border: 'none',
                    background: '#16a34a',
                    color: '#fff',
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: 'pointer',
                  }}
                >
                  ✅ {tr('Stapel abschließen & archivieren', 'Complete & archive batch', 'Stapel afronden & archiveren')}
                </button>
                <button
                  type="button"
                  onClick={() => { resetBatchScanned(); setListe(listBatch()); setPos(0); }}
                  style={{
                    padding: '4px 10px',
                    borderRadius: 6,
                    border: '1px solid #22c55e',
                    background: 'transparent',
                    color: 'inherit',
                    fontSize: 12,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  ↺ {tr('Nochmal von vorn', 'Start over', 'Opnieuw beginnen')}
                </button>
              </div>
            </div>
          )}

          {/* 🔄 Hinweis beim Wechsel zum nächsten Paketdienst innerhalb eines gemischten Stapels */}
          {carrierSwitchedHere && (
            <div
              data-testid="batch-carrier-switch-notice"
              style={{
                borderRadius: 10,
                padding: '8px 12px',
                marginBottom: 8,
                textAlign: 'center',
                fontWeight: 800,
                fontSize: 13.5,
                background: currentDienstStil.bg,
                border: `2px solid ${currentDienstStil.border}`,
                color: currentDienstStil.text,
              }}
            >
              🔄 {tr('Nächster Paketdienst:', 'Next carrier:', 'Volgende pakketdienst:')}{' '}
              <span>{currentDienstStil.icon} {currentDienst || tr('Sonstige', 'Other', 'Overige')}</span>
              {prevDienst ? ` (${tr('vorher:', 'previously:', 'eerder:')} ${prevDienst})` : ''}
            </div>
          )}

          {/* 📌 Klare Status-Box: Welcher Paketdienst + Habe ich dieses Paket schon gescannt oder nicht? */}
          <div
            data-testid="batch-scan-status"
            style={{
              borderRadius: 10,
              padding: '8px 12px',
              marginBottom: 8,
              textAlign: 'center',
              background: e.scanned
                ? (dark ? 'rgba(22,163,74,0.22)' : '#dcfce7')
                : (dark ? 'rgba(2,132,199,0.18)' : '#e0f2fe'),
              border: `2px solid ${e.scanned ? '#22c55e' : '#0284c7'}`,
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, flexWrap: 'wrap' }}>
              <span
                data-testid="batch-current-carrier"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 4,
                  padding: '2px 9px',
                  borderRadius: 999,
                  fontSize: 13,
                  fontWeight: 800,
                  border: `1.5px solid ${currentDienstStil.border}`,
                  background: currentDienstStil.bg,
                  color: currentDienstStil.text,
                }}
              >
                {currentDienstStil.icon} {currentDienst || tr('Sonstige', 'Other', 'Overige')}
              </span>
              <span style={{ fontWeight: 800, fontSize: 15 }} data-testid="batch-pos">
                {selectedCarrier
                  ? `${tr('Paket', 'Parcel', 'Pakket')} ${posInSlice + 1} / ${carrierSliceIndices.length} (${currentDienst || tr('Sonstige', 'Other', 'Overige')}):`
                  : `${tr('Paket', 'Parcel', 'Pakket')} ${effectiveGlobalPos + 1} / ${liste.length}${expectedCount > 0 && expectedCount !== liste.length ? ` (${tr('Soll Bote', 'Courier', 'Bezorger')}: ${expectedCount})` : ''}:`}
              </span>
              {e.scanned ? (
                <span style={{ color: dark ? '#4ade80' : '#15803d', fontWeight: 800, fontSize: 15 }}>
                  ✅ {t('batch.alreadyScanned') || tr('Bereits gescannt', 'Already scanned', 'Al gescand')}
                  {e.scannedAt ? ` (${uhr(e.scannedAt)})` : ''}
                </span>
              ) : (
                <span style={{ color: dark ? '#38bdf8' : '#0369a1', fontWeight: 800, fontSize: 15 }}>
                  ⏳ {t('batch.notScannedYet') || tr('Noch offen — jetzt scannen', 'Still open — scan now', 'Nog open — nu scannen')}
                </span>
              )}
              {e.scanned && (
                <button
                  type="button"
                  onClick={toggleCurrentScanned}
                  style={{
                    padding: '2px 8px',
                    borderRadius: 6,
                    border: '1px solid currentColor',
                    background: 'transparent',
                    color: 'inherit',
                    fontSize: 11,
                    fontWeight: 700,
                    cursor: 'pointer',
                  }}
                >
                  ↺ {tr('Offen setzen', 'Mark open', 'Open zetten')}
                </button>
              )}
            </div>
            {!e.scanned && prevItem && prevItem.scanned && (
              <div style={{ fontSize: 12, opacity: 0.85, marginTop: 3 }}>
                ✓ {tr('Zuletzt davor erledigt:', 'Previously completed:', 'Laatst daarvoor afgerond:')} <strong>#{selectedCarrier ? posInSlice : effectiveGlobalPos} {prevItem.name || prevItem.tracking || '—'}</strong>
                {prevItem.scannedAt ? ` (${uhr(prevItem.scannedAt)})` : ''}
              </div>
            )}
          </div>

          <div style={{ textAlign: 'center', margin: '2px 0 8px' }}>
            <div style={{ fontSize: 20, fontWeight: 700 }}>{e.name || '—'}</div>
            <div style={{ fontSize: 14, opacity: 0.8 }}>
              {e.regalplatz ? `${t('batch.shelf')} ${e.regalplatz}` : ''}
              {e.tracking ? ` · ${e.tracking}` : ''}
              {e.at ? ` · 🕐 ${uhr(e.at)}` : ''}
            </div>
          </div>

          {code && <BarcodeBild text={code.text} format={code.format} />}

          {/* Inhalt groß + in 4er-Gruppen → notfalls von Hand ins Zusteller-Gerät tippen */}
          <div
            data-testid="batch-code-text"
            style={{
              fontSize: 20, fontFamily: 'monospace', fontWeight: 700, letterSpacing: '0.04em',
              textAlign: 'center', marginTop: 8, wordBreak: 'break-all', lineHeight: 1.4,
            }}
          >
            {code ? code.text.replace(/\x1d/g, '·').replace(/(.{4})/g, '$1 ').trim() : ''}
          </div>
          {code && code.abgeleitet && (
            <div style={{ fontSize: 12, textAlign: 'center', opacity: 0.7, marginTop: 4 }}>
              {t('batch.derived')}
            </div>
          )}

          {/* Aktionszeile für Problemfälle: Originalfoto (zoombar) & ggf. zweites Barcode-Format auf dem Label */}
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <button
              type="button"
              data-testid="batch-photo-btn"
              onClick={oeffneOriginalfoto}
              disabled={fotoLoading}
              style={btn(false, { flex: 1, fontSize: 14, padding: '10px 12px' })}
            >
              🖼️ {fotoLoading
                ? (t('common.loading') || tr('Lädt…', 'Loading…', 'Laden…'))
                : (t('batch.showPhoto') || tr('Originalfoto (zoombar)', 'Original photo (zoomable)', 'Originele foto (zoombaar)'))}
            </button>

            {e.codes.length > 1 && (
              <button
                type="button"
                data-testid="batch-other"
                title={t('batch.otherCodeExplain') || tr(
                  'Auf diesem Etikett wurden mehrere Barcodes erkannt (z. B. Strichcode + QR/DataMatrix). Hiermit schaltest du um.',
                  'Multiple barcodes were detected on this label (e.g. 1D barcode + QR/DataMatrix). Click to switch.',
                  'Op dit label zijn meerdere barcodes herkend (bijv. streepjescode + QR/DataMatrix). Hiermee schakel je om.'
                )}
                style={btn(false, { flex: 1, fontSize: 14, padding: '10px 12px' })}
                onClick={() => setWahl((w) => ({ ...w, [e.id]: (idx + 1) % e.codes.length }))}
              >
                🔁 {t('batch.otherCode', { n: idx + 1, total: e.codes.length })}
              </button>
            )}
          </div>

          {fotoError && (
            <div style={{ fontSize: 12, color: '#f59e0b', textAlign: 'center', marginTop: 6 }}>
              ℹ️ {fotoError}
            </div>
          )}

          {/* Navigation: Vor / Zurück (bzw. beim letzten Paket als gescannt markieren) */}
          <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
            {carrierSliceIndices.length > 1 && (
              <button
                type="button"
                style={btn(false, { flex: 1 })}
                disabled={posInSlice === 0}
                onClick={() => {
                  const prevIdx = carrierSliceIndices[Math.max(0, posInSlice - 1)];
                  if (prevIdx !== undefined) setPos(prevIdx);
                }}
              >
                ◀
              </button>
            )}
            {posInSlice < carrierSliceIndices.length - 1 ? (
              <button
                type="button"
                data-testid="batch-next"
                style={btn(true, { flex: 3 })}
                onClick={weiterZumNaechsten}
              >
                ✓ {t('batch.next')} ▶ ({posInSlice + 2}/{carrierSliceIndices.length})
              </button>
            ) : (
              <button
                type="button"
                data-testid="batch-mark-last"
                style={btn(true, {
                  flex: 3,
                  background: e.scanned ? '#15803d' : '#16a34a',
                  opacity: e.scanned ? 0.9 : 1,
                })}
                onClick={() => {
                  markBatchItemScanned(e.id, true);
                  setListe(listBatch());
                }}
              >
                {e.scanned
                  ? `✅ ${trDyn(lang, 'd23', { count: carrierSliceIndices.length })}`
                  : `✅ ${tr('Letztes Paket als gescannt markieren', 'Mark last parcel as scanned', 'Laatste pakket als gescand markeren')}`}
              </button>
            )}
          </div>

          {/* ⏸️ Zentraler Pause-Button weiter unten mittig (speichert genau dieses Paket für nach der Pause) */}
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: 12 }}>
            <button
              type="button"
              data-testid="batch-pause-center"
              onClick={onClose}
              title={t('batch.pauseHint') || tr(
                'Pausieren — zeigt beim nächsten Öffnen genau dieses Paket wieder an',
                'Pause — resumes at this exact parcel next time you open the batch',
                'Pauzeren — toont bij het volgende openen precies dit pakket weer'
              )}
              style={{
                width: '100%',
                maxWidth: 420,
                padding: '13px 16px',
                borderRadius: 12,
                fontSize: 16,
                fontWeight: 800,
                cursor: 'pointer',
                border: `2px solid ${dark ? '#475569' : '#94a3b8'}`,
                background: dark ? '#1e293b' : '#e2e8f0',
                color: dark ? '#f8fafc' : '#1e293b',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
              }}
            >
              ⏸️ {trDyn(lang, 'd24', { pos: pos + 1, total: liste.length })}
            </button>
          </div>

          <div style={{ fontSize: 12, textAlign: 'center', marginTop: 8, opacity: 0.7 }}>
            🔆 {tr('Automatisches Abdunkeln deaktiviert', 'Screen auto-dimming disabled', 'Automatisch dimmen uitgeschakeld')}
            {micOn ? ` · 🎙️ ${t('batch.micActiveHint') || tr('DHL-Scanner-Piep schaltet automatisch weiter', 'Scanner beep advances automatically', 'Scanner-piep schakelt automatisch verder')}` : ''}
          </div>
        </>
      )}

      {/* ── Übersicht aller Pakete im aktuellen Stapel (mit Paketdienst-Badge) ── */}
      {liste.length > 0 && uebersicht && !showHistory && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {liste.map((x, i) => {
            if (selectedCarrier && erkenneStapelDienst(x).toLowerCase() !== activeCarrierTarget.toLowerCase()) {
              return null;
            }
            const xDienst = erkenneStapelDienst(x);
            const xStil = getStapelDienstStil(xDienst, dark);
            return (
              <div
                key={x.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px', borderRadius: 8,
                  background: i === effectiveGlobalPos ? (dark ? '#1e293b' : '#e0f2fe') : (dark ? '#23272f' : '#fff'),
                  border: `1px solid ${i === effectiveGlobalPos ? '#0284c7' : (dark ? '#333' : '#e3e3e3')}`,
                }}
              >
                <span style={{ width: 28, opacity: 0.9, fontWeight: 700 }}>
                  {x.scanned ? '✅' : `${i + 1}.`}
                </span>
                <span
                  style={{ flex: 1, cursor: 'pointer' }}
                  onClick={() => { setPos(i); setUebersicht(false); }}
                >
                  <span
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 3,
                      padding: '1px 7px',
                      marginRight: 6,
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 800,
                      border: `1px solid ${xStil.border}`,
                      background: xStil.bg,
                      color: xStil.text,
                    }}
                  >
                    {xStil.icon} {xDienst || tr('Sonstige', 'Other', 'Overige')}
                  </span>
                  <strong>{x.name || '—'}</strong>{' '}
                  <span style={{ opacity: 0.7, fontSize: 13 }}>{x.regalplatz}</span>
                  {x.at && (
                    <span style={{ marginLeft: 6, fontSize: 11, opacity: 0.65 }}>
                      ({uhr(x.at)})
                    </span>
                  )}
                  {x.scanned && x.scannedAt && (
                    <span style={{ marginLeft: 6, fontSize: 11, color: '#22c55e', fontWeight: 700 }}>
                      ✓ {tr('gescannt', 'scanned', 'gescand')} {uhr(x.scannedAt)}
                    </span>
                  )}
                  {x.tracking && <div style={{ fontSize: 11, fontFamily: 'monospace', opacity: 0.65 }}>{x.tracking}</div>}
                </span>
                <button
                  type="button"
                  onClick={() => removeFromBatch(x.id)}
                  aria-label="remove"
                  style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: 'inherit' }}
                >
                  🗑️
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* ── 🗂️ Alle Stapel / Lieferungen der letzten 7 Tage (automatisch nach 15-Min.-Abstand getrennt, mit Erledigt-Status & Uhrzeit) ── */}
      {showHistory && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{
            display: 'flex', flexWrap: 'wrap', gap: 8, padding: 10, borderRadius: 10,
            background: dark ? '#23272f' : '#fff', border: `1px solid ${dark ? '#333' : '#ddd'}`,
          }}>
            <div style={{ flex: '1 1 130px' }}>
              <label style={{ fontSize: 11, opacity: 0.7, display: 'block', marginBottom: 2 }}>
                📅 {t('batch.filterDate') || tr('Tag', 'Date', 'Dag')}
              </label>
              <input
                type="date"
                value={filterDate}
                onChange={(ev) => setFilterDate(ev.target.value)}
                style={{
                  width: '100%', padding: '6px 8px', borderRadius: 6,
                  border: `1px solid ${dark ? '#444' : '#ccc'}`,
                  background: dark ? '#15181d' : '#f9fafb', color: 'inherit', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ flex: '1 1 90px' }}>
              <label style={{ fontSize: 11, opacity: 0.7, display: 'block', marginBottom: 2 }}>
                🕐 {t('common.from') || tr('Von', 'From', 'Van')}
              </label>
              <input
                type="time"
                value={filterTimeFrom}
                onChange={(ev) => setFilterTimeFrom(ev.target.value)}
                style={{
                  width: '100%', padding: '6px 8px', borderRadius: 6,
                  border: `1px solid ${dark ? '#444' : '#ccc'}`,
                  background: dark ? '#15181d' : '#f9fafb', color: 'inherit', boxSizing: 'border-box',
                }}
              />
            </div>
            <div style={{ flex: '1 1 90px' }}>
              <label style={{ fontSize: 11, opacity: 0.7, display: 'block', marginBottom: 2 }}>
                🕐 {t('common.to') || tr('Bis', 'To', 'Tot')}
              </label>
              <input
                type="time"
                value={filterTimeTo}
                onChange={(ev) => setFilterTimeTo(ev.target.value)}
                style={{
                  width: '100%', padding: '6px 8px', borderRadius: 6,
                  border: `1px solid ${dark ? '#444' : '#ccc'}`,
                  background: dark ? '#15181d' : '#f9fafb', color: 'inherit', boxSizing: 'border-box',
                }}
              />
            </div>
            {(filterDate || filterTimeFrom || filterTimeTo) && (
              <button
                type="button"
                onClick={() => { setFilterDate(''); setFilterTimeFrom(''); setFilterTimeTo(''); }}
                style={{ alignSelf: 'flex-end', padding: '6px 10px', borderRadius: 6, border: 'none', background: '#ef4444', color: '#fff', cursor: 'pointer', fontSize: 12 }}
              >
                ✕ {t('common.reset') || 'Reset'}
              </button>
            )}
          </div>

          <div style={{ fontSize: 12, opacity: 0.8, padding: '0 4px' }}>
            💡 {tr(
              'Lieferungen mit mehr als 15 Minuten Abstand werden automatisch als eigener Stapel angelegt. Du kannst jeden Stapel einzeln öffnen oder direkt als ✅ Erledigt markieren.',
              'Deliveries more than 15 minutes apart are automatically grouped into separate batches. You can open any batch or mark it directly as ✅ Done.',
              'Leveringen met meer dan 15 minuten tussenpoos worden automatisch als aparte stapel aangemaakt. Je kunt elke stapel afzonderlijk openen of direct als ✅ Afgerond markeren.'
            )}
          </div>

          {/* 0) Aktuell offener Stapel ganz oben in der Liste */}
          {liste.length > 0 && !filterDate && !filterTimeFrom && !filterTimeTo && (
            <div
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
                padding: '12px 14px', borderRadius: 10,
                background: dark ? '#241f3a' : '#f3f0ff',
                border: '2px solid #7b61ff',
              }}
            >
              <div style={{ flex: '1 1 220px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{
                    fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                    background: '#7b61ff', color: '#fff',
                  }}>
                    🟣 {trDyn(lang, 'd25', { scanned: scannedCount, total: liste.length })}
                  </span>
                  {expectedCount > 0 && (
                    <span style={{ fontSize: 11.5, fontWeight: 700, opacity: 0.9 }}>
                      🚚 {tr('Soll Bote', 'Courier', 'Bezorger')}: {liste.length}/{expectedCount}
                    </span>
                  )}
                </div>
                <div style={{ fontWeight: 800, fontSize: 15, marginTop: 4 }}>
                  📦 {liste.length} {pkgWord(liste.length)} · {activeTimeInfo ? activeTimeInfo.fullLabel : tr('Aktuell', 'Current', 'Huidig')}
                </div>
                <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                  {liste.slice(0, 4).map((it) => it.name || it.tracking).filter(Boolean).join(', ')}
                  {liste.length > 4 ? ' …' : ''}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  onClick={() => { setShowHistory(false); setUebersicht(false); }}
                  style={btn(true, { padding: '8px 12px', fontSize: 13, background: '#7b61ff' })}
                >
                  ▶ {tr('Weiter scannen', 'Continue scanning', 'Verder scannen')}
                </button>
                <button
                  type="button"
                  onClick={handleCompleteActiveBatch}
                  style={btn(false, {
                    padding: '8px 12px', fontSize: 13,
                    border: '1px solid #22c55e',
                    background: dark ? '#14532d' : '#dcfce7',
                    color: dark ? '#86efac' : '#166534',
                  })}
                >
                  ✅ {tr('Als erledigt markieren', 'Mark as done', 'Als afgerond markeren')}
                </button>
              </div>
            </div>
          )}

          {/* 1) Gespeicherte Stapel (aus der Stapel-Historie, automatisch getrennt nach 15-Min.-Lücke) */}
          {filteredHistory.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {filteredHistory.map((h) => {
                const info = formatDayAndRange(h.startedAt || h.finishedAt, h.finishedAt, lang);
                const isDone = !!(h.completed || (h.count > 0 && (h.scannedCount || 0) >= h.count));
                const doneTime = h.completedAt ? uhr(h.completedAt) : uhr(h.finishedAt);
                const sameDayAsActive = liste.length > 0 && activeStartTs && new Date(activeStartTs).toDateString() === new Date(h.finishedAt).toDateString();
                const hCarriers = groupBatchByCarrier(h.items || []);
                return (
                  <div
                    key={h.id}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
                      padding: '12px 14px', borderRadius: 10,
                      background: isDone ? (dark ? '#17251e' : '#f0fdf4') : (dark ? '#23272f' : '#fff'),
                      border: `1px solid ${isDone ? '#22c55e' : (dark ? '#3b4252' : '#cbd5e1')}`,
                    }}
                  >
                    <div style={{ flex: '1 1 220px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                        {isDone ? (
                          <span style={{
                            fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                            background: '#16a34a', color: '#fff',
                          }}>
                            ✅ {tr('Erledigt', 'Done', 'Afgerond')} {doneTime ? `(${doneTime})` : ''} · {h.count}/{h.count}
                          </span>
                        ) : (
                          <span style={{
                            fontSize: 11, fontWeight: 800, padding: '2px 8px', borderRadius: 999,
                            background: '#f59e0b', color: '#fff',
                          }}>
                            ⏳ {trDyn(lang, 'd26', { scanned: h.scannedCount || 0, total: h.count })}
                          </span>
                        )}
                        {hCarriers.map((cg) => {
                          const st = getStapelDienstStil(cg.dienst, dark);
                          return (
                            <span
                              key={cg.dienst || 'other'}
                              style={{
                                fontSize: 11,
                                fontWeight: 800,
                                padding: '2px 7px',
                                borderRadius: 999,
                                border: `1px solid ${st.border}`,
                                background: st.bg,
                                color: st.text,
                              }}
                            >
                              {st.icon} {cg.count}× {cg.dienst || tr('Sonstige', 'Other', 'Overige')}
                            </span>
                          );
                        })}
                        {h.expectedCount > 0 && (
                          <span style={{ fontSize: 11.5, fontWeight: 700, opacity: 0.85 }}>
                            🚚 {tr('Soll Bote', 'Courier', 'Bezorger')}: {h.count}/{h.expectedCount}
                          </span>
                        )}
                      </div>
                      <div style={{ fontWeight: 700, fontSize: 15, marginTop: 4 }}>
                        📦 {h.count} {pkgWord(h.count)} · {info.fullLabel}
                      </div>
                      <div style={{ fontSize: 12, opacity: 0.75, marginTop: 2 }}>
                        {(h.items || []).slice(0, 4).map((it) => it.name || it.tracking).filter(Boolean).join(', ')}
                        {(h.items || []).length > 4 ? ' …' : ''}
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'center' }}>
                      {!isDone ? (
                        <button
                          type="button"
                          onClick={() => handleToggleHistoryCompleted(h.id, true)}
                          style={btn(false, {
                            padding: '8px 12px', fontSize: 12.5, whiteSpace: 'nowrap',
                            border: '1px solid #22c55e',
                            background: dark ? '#14532d' : '#dcfce7',
                            color: dark ? '#86efac' : '#166534',
                          })}
                        >
                          ✅ {tr('Als erledigt markieren', 'Mark as done', 'Als afgerond markeren')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          onClick={() => handleToggleHistoryCompleted(h.id, false)}
                          style={btn(false, { padding: '7px 10px', fontSize: 12, whiteSpace: 'nowrap' })}
                        >
                          ↺ {tr('Offen setzen', 'Mark open', 'Open zetten')}
                        </button>
                      )}
                      {sameDayAsActive && (
                        <button
                          type="button"
                          onClick={() => handleMergeHistoryIntoActive(h.id)}
                          title={tr(
                            'Mit dem aktuellen Stapel zu einem gemeinsamen Stapel verbinden',
                            'Combine with the current batch into a single batch',
                            'Samenvoegen met de huidige stapel tot één stapel'
                          )}
                          style={btn(false, { padding: '7px 10px', fontSize: 12, whiteSpace: 'nowrap' })}
                        >
                          🔗 {tr('Verbinden', 'Combine', 'Samenvoegen')}
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={() => handleRestore(h.id)}
                        style={btn(true, { padding: '8px 12px', fontSize: 12.5, whiteSpace: 'nowrap' })}
                      >
                        {isDone
                          ? `↩️ ${tr('Wieder öffnen', 'Reopen', 'Heropenen')}`
                          : `▶ ${tr('Stapel öffnen', 'Open batch', 'Stapel openen')}`}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* 2) Weitere Liefer-Sitzungen aus dem Paketbestand der letzten 7 Tage (ebenfalls nach 15-Min.-Lücke getrennt) */}
          {dailyPackageGroups.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: filteredHistory.length > 0 ? 4 : 0 }}>
              <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.8, padding: '2px 4px' }}>
                📅 {tr(
                  'Weitere Lieferungen aus dem Paketbestand (nach 15-Min.-Abstand gruppiert):',
                  'Other deliveries from parcel inventory (grouped by 15-min gap):',
                  'Overige leveringen uit de pakketvoorraad (gegroepeerd op 15 min. tussenpoos):'
                )}
              </div>
              {dailyPackageGroups.map((g) => {
                const info = formatDayAndRange(g.minTs, g.maxTs, lang);
                const gCarriers = groupBatchByCarrier(g.itemsWithTracking || []);
                return (
                  <div
                    key={`session-${g.sessionKey}`}
                    style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap',
                      padding: '12px 14px', borderRadius: 10,
                      background: dark ? '#1e293b' : '#ffffff',
                      border: `1px solid ${dark ? '#334155' : '#cbd5e1'}`,
                    }}
                  >
                    <div style={{ flex: '1 1 220px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', marginBottom: 3 }}>
                        <span style={{ fontWeight: 700, fontSize: 15 }}>
                          📦 {g.itemsWithTracking.length} {pkgWord(g.itemsWithTracking.length)} · {info.fullLabel}
                        </span>
                        {gCarriers.map((cg) => {
                          const st = getStapelDienstStil(cg.dienst, dark);
                          return (
                            <span
                              key={cg.dienst || 'other'}
                              style={{
                                fontSize: 11,
                                fontWeight: 800,
                                padding: '2px 7px',
                                borderRadius: 999,
                                border: `1px solid ${st.border}`,
                                background: st.bg,
                                color: st.text,
                              }}
                            >
                              {st.icon} {cg.count}× {cg.dienst || tr('Sonstige', 'Other', 'Overige')}
                            </span>
                          );
                        })}
                      </div>
                      {g.itemsWithTracking.length > 0 && (
                        <div style={{ fontSize: 12, opacity: 0.8, marginTop: 2 }}>
                          {g.itemsWithTracking.slice(0, 4).map((it) => it.name || it.trackingNumber).filter(Boolean).join(', ')}
                          {g.itemsWithTracking.length > 4 ? ' …' : ''}
                        </div>
                      )}
                      {g.itemsWithoutTracking.length > 0 && (
                        <div style={{ fontSize: 11, color: '#d97706', marginTop: 3 }}>
                          ℹ️ {trDyn(lang, 'd27', { count: g.itemsWithoutTracking.length, names: g.itemsWithoutTracking.map((it) => it.name || '—').slice(0, 3).join(', ') })}
                        </div>
                      )}
                    </div>
                    {g.itemsWithTracking.length > 0 && (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <button
                          type="button"
                          onClick={() => handleMarkDailyGroupCompleted(g.itemsWithTracking)}
                          style={btn(false, {
                            padding: '8px 12px', fontSize: 12.5, whiteSpace: 'nowrap',
                            border: '1px solid #22c55e',
                            background: dark ? '#14532d' : '#dcfce7',
                            color: dark ? '#86efac' : '#166534',
                          })}
                        >
                          ✅ {tr('Als erledigt markieren', 'Mark as done', 'Als afgerond markeren')}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleOpenDailyGroup(g.itemsWithTracking)}
                          style={btn(true, { padding: '8px 12px', fontSize: 12.5, whiteSpace: 'nowrap' })}
                        >
                          ▶ {tr('Als Stapel öffnen', 'Open as batch', 'Als stapel openen')} ({g.itemsWithTracking.length})
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {filteredHistory.length === 0 && dailyPackageGroups.length === 0 && liste.length === 0 && (
            <div style={{ padding: 24, textAlign: 'center', opacity: 0.7 }}>
              {t('batch.noHistoryFound') || tr('Keine früheren Stapel für diesen Zeitraum gefunden.', 'No previous batches found for this period.', 'Geen eerdere stapels gevonden voor deze periode.')}
            </div>
          )}
        </div>
      )}

      {/* ── Fußzeile (mit extra Abstand nach unten für Android-Navigationsleiste) ── */}
      {(uebersicht || showHistory) && (
        <div style={{ display: 'flex', gap: 8, marginTop: 'auto', paddingTop: 14, marginBottom: 8, flexWrap: 'wrap' }}>
          <button
            type="button"
            style={btn(false, { flex: 1, fontSize: 14 })}
            onClick={() => { setShowHistory(false); setUebersicht(false); }}
          >
            ◀ {t('batch.back') || tr('Zurück zum aktuellen Stapel', 'Back to current batch', 'Terug naar huidige stapel')}
          </button>
          <button
            type="button"
            style={btn(false, { flex: 1, fontSize: 14 })}
            onClick={onClose}
          >
            ⏸️ {t('batch.pause') || tr('Pause / Schließen', 'Pause / Close', 'Pauze / Sluiten')}
          </button>
        </div>
      )}

      {/* 🔍 Vollbild-Zoom für Originalfoto */}
      {zoomSrc && <ZoomImageViewer src={zoomSrc} onClose={schliesseFoto} />}
    </div>
  );
}

